import React from 'react';
import { useQuery } from '@tanstack/react-query';

import { jzkApi } from '../services/api';
import { JZKValidityProcessedItem } from '../types';
import { useTranslation } from '../utils/i18n';

import ArrowRightIcon from './ArrowRightIcon';

interface JZKOlderDataProps {
  onShowHistorical: () => void;
}

interface ValidityInfo {
  jzkNumber: string;
  year: number;
  validityDate: string;
}

const parseDateStringUTC = (dateString: string): Date => {
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

// Helper to subtract days from a date string (UTC-safe)
const subtractDays = (dateString: string, days: number): string => {
  const date = parseDateStringUTC(dateString);
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().split('T')[0];
};

// Fallback calculation when API data is not available
const getFallbackValidityInfo = (): ValidityInfo => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  const jzkNumber = Math.ceil(currentMonth / 2);
  const nextMonth = jzkNumber * 2 + 1;
  const nextYear = nextMonth > 12 ? currentYear + 1 : currentYear;
  const validityMonth = nextMonth > 12 ? 1 : nextMonth;
  const validityDate = new Date(Date.UTC(nextYear, validityMonth - 1, 2));

  return {
    jzkNumber: String(jzkNumber).padStart(2, '0'),
    year: currentYear,
    validityDate: validityDate.toISOString().split('T')[0],
  };
};

// Get validity info from API data - finds the previous JŽK that has expired
const getValidityInfoFromData = (
  data: Record<number, JZKValidityProcessedItem[]>,
): ValidityInfo | null => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Flatten all items and sort by validFrom date descending (newest first)
  const allItems = Object.values(data).flat();
  if (allItems.length < 2) {
    return null;
  }

  const sorted = [...allItems].sort(
    (a, b) => new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime(),
  );

  // Find the current JŽK (first one with validFrom <= today)
  let currentIndex = -1;
  for (let i = 0; i < sorted.length; i++) {
    const validFromDate = new Date(sorted[i].validFrom);
    validFromDate.setHours(0, 0, 0, 0);

    if (validFromDate <= today) {
      currentIndex = i;
      break;
    }
  }

  // Need both current and previous JŽK
  if (currentIndex < 0 || currentIndex + 1 >= sorted.length) {
    return null;
  }

  const current = sorted[currentIndex];
  const previous = sorted[currentIndex + 1];

  return {
    jzkNumber: String(previous.number).padStart(2, '0'),
    year: previous.year,
    // Previous JŽK expired the day before current JŽK became valid
    validityDate: subtractDays(current.validFrom, 1),
  };
};

// Function to format date for display based on language
const formatDate = (dateString: string, language: string): string => {
  const date = parseDateStringUTC(dateString);

  // Map language codes to locale codes
  const localeMap: Record<string, string> = {
    cs: 'cs-CZ',
    en: 'en-US',
    sk: 'sk-SK',
    de: 'de-DE',
  };

  const locale = localeMap[language] || 'cs-CZ';

  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const JZKOlderData = ({ onShowHistorical }: JZKOlderDataProps) => {
  const { t, language } = useTranslation();

  const { data: validityData } = useQuery({
    queryKey: ['jzk-validity'],
    queryFn: jzkApi.getValidity,
  });

  const validityInfo = React.useMemo(() => {
    if (!validityData) {
      return getFallbackValidityInfo();
    }
    return getValidityInfoFromData(validityData) ?? getFallbackValidityInfo();
  }, [validityData]);

  return (
    <div className="jzk-older-data-section">
      <div className="jzk-section-header">
        <div className="section-divider"></div>
        <h2 className="section-title">{t('section.olderData')}</h2>
      </div>

      <div className="jzk-older-data-content">
        <h3 className="older-data-title">{t('section.historicalJzk')}</h3>
        <p className="older-data-description">{t('section.historicalDescription')}</p>
        <p className="validity-info">
          {t('section.validityExpires', {
            date: formatDate(validityInfo.validityDate, language),
            number: validityInfo.jzkNumber,
            year: validityInfo.year.toString(),
          })}
        </p>
        <button className="btn-show-older" onClick={onShowHistorical}>
          {t('button.show')} <ArrowRightIcon className="arrow-icon" />
        </button>
      </div>
    </div>
  );
};

export default JZKOlderData;
